import 'cui/sass/components/grid.scss';
import React, { useRef, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import { RegisteredApplicationDto } from 'api/NSwagApi/api';
import RightSideDrawer from 'cui/components/rightSideDrawer/RightSideDrawer';
import { ClearFiltersButton } from 'cui/components/table/clear-filter/clear-filters-button';
import { Button, LoadIndicator } from 'devextreme-react';
import {
  Column,
  DataGrid,
  HeaderFilter,
  Item,
  LoadPanel,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  Sorting
} from 'devextreme-react/data-grid';
import { useEffectSingleCalls } from 'hooks/useEffectSingleCalls';
import { useAppDispatch, useAppSelector } from 'store/app/hooks';
import { RootState } from 'store/app/store';
import { setBannerCount, setBannerText } from '../../../cui/components/breadcrumb/breadcrumb.slice';
import EditButton from '../EditButton';
import 'pages/admin/dataGrid.scss';
import PageHeader from 'cui/components/pageHeader/PageHeader';
import { ApplicationRegister } from './applicationRegister';
import { ApplicationEdit } from './applicationEdit';
import EmptyGrid from '../EmptyGrid';
import { SystemAdminWorkspacePermission } from 'utils/users';
import { ErrorCodesUI, GetApiErrorCode } from 'constants/ErrorCodes';
import { showErrorSnackbar } from 'cui/components/snackbar/Snackbar.slice';

export const Applications: React.FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const dataGridRef = useRef<DataGrid>(null);
  const [error, setError] = useState<string>('');
	const [appsLoaded, setAppsLoaded] = useState<boolean>(false);
  const [isFilterEnabled, setIsFilterEnabled] = useState<boolean>(false);
  const [isInCreationMode, setIsInCreationMode] = useState(false);
  const [rowData, setRowData] = useState<any>([]);
  const [editStatus, setEditStatus] = useState<boolean>(false);
  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [recordsCount, setRecordsCount] = useState<any>();

  const registeredApplications = useAppSelector(
    (state: RootState) => state.applications.registeredApplications
  );

  const registerApplicationText = 'Register Application';
  const editApplicationText = 'Edit Application';
  const columnWidths = ["20%", "22.5%", "22.5%", "22.5%", "7.5%", "5%"];
  const userFeatures = useAppSelector((state: RootState) => state.users.userRoleFeatures);

  const applicationFeatures = registeredApplications.flatMap(application => application.features);
  const adminFeatures = Array.from(new Set(applicationFeatures
      .filter(appFeature => appFeature!.featureTypeId === 0)
      .map(appFeature => appFeature!.feature)));
  const viewApplicationFeatures = Array.from(new Set(applicationFeatures
    .filter(appFeature => appFeature!.featureTypeId === 1)
    .map(appFeature => appFeature!.feature)));
    const executiveReportFeatures = Array.from(new Set(applicationFeatures
      .filter(appFeature => appFeature!.featureTypeId === 2)
      .map(appFeature => appFeature!.feature)));

  useEffectSingleCalls(() => {
      fetchData().catch((error) => {
        console.error(error);
      });
  }, []);

  const fetchData = async () => {
    try {
        if(registeredApplications.length === 0){
          setError(`${ErrorCodesUI.NoDataToDisplay} - No data to display`);
          showErrorSnackbar(dispatch, `${ErrorCodesUI.NoDataToDisplay} - No data to display.`);
        }
        dispatch(setBannerText('Registered Applications'));
        dispatch(setBannerCount(registeredApplications.length));
    } catch (e: any) {
        setError(`${GetApiErrorCode(e)} - An error occured while loading applications.`);
        showErrorSnackbar(dispatch, `${GetApiErrorCode(e)} - An error occured while loading applications.`);
    };
    setAppsLoaded(true);
  };

  const userAccessibleApplications = () => {
    var accessibleApps = userFeatures.includes(SystemAdminWorkspacePermission)
    ? registeredApplications
    : registeredApplications.filter(app =>
      app.features?.some(featureDto => featureDto.featureTypeId === 0 && userFeatures!.includes(featureDto.feature!.toLowerCase()))
  );
  return accessibleApps.map(app => ({
    ...app,
    isActiveText: setIsActiveSearchColumn(app.isActive!)
  }));
  };

  const setIsActiveSearchColumn = (isActive: boolean): string => {
    return isActive ? "Active" : "Inactive";
  };

  const setActionColumn = (event: any) => {
    const handleEditClick = () => {
      setEditStatus(true);
      handleEditApplication(event.data as RegisteredApplicationDto);
    };

    return (
      <EditButton handleClick={handleEditClick} />
    );
  };

  const onDrawerCloseClick = () => {
    setIsInCreationMode(false);
    return true;
  };

  const enterCreationMode = () => {
    setIsInCreationMode(true);
    setEditStatus(false);
    setDrawerTitle((prev) => registerApplicationText);
  };

  const handleApplicationRegistered = () => {
    if(registeredApplications.length === 0){
      setAppsLoaded(false);
      fetchData();
    };
  };

  const handleEditApplication = (rowData: RegisteredApplicationDto) => {
    setIsInCreationMode(true);
    setDrawerTitle((prev) => editApplicationText);

    if (rowData) {
      setRowData(rowData);
    };
  };

  const setNameColumn = (event: any) => {
    return (
      <div className="grid-column">
        {event.data.applicationName}
      </div>
    )
  }

  const setFeatureColumn = (event: any, featureTypeId: number) => {
    const adminFeature = event.data.features.find(
      (feature: { featureTypeId: number }) => feature.featureTypeId === featureTypeId);
    return <>{adminFeature ? adminFeature.feature : null}</>
  };

  const renderAdminFeatureColumn = (event: any) => setFeatureColumn(event, 0);
  const renderViewFeatureColumn = (event: any) => setFeatureColumn(event, 1);
  const renderExecutiveReportsFeatureColumn = (event: any) => setFeatureColumn(event, 2);

  const adminFeatureFilterData = adminFeatures.map((adminFeature) => ({
    text: adminFeature,
    value: adminFeature
  }));
  
  const viewApplicationFeatureFilterData = viewApplicationFeatures.map((viewAppFeature) => ({
    text: viewAppFeature,
    value: viewAppFeature
  }));

  const executiveReportsFeatureFilterData = executiveReportFeatures.map((execReportFeature) => ({
    text: execReportFeature,
    value: execReportFeature
  }));

  const handleFilter = () => {
    setIsFilterEnabled(!isFilterEnabled);
  }

  const handlePageSizeChange = (e: any) => {
    resetGridState(dataGridRef);
  };
  
  const handleSortChanges = (e: any) => {
    try {
      if (e.fullName?.includes("columns") && e.fullName?.includes("sortOrder"))
        resetGridState(dataGridRef);
    } 
    catch (error) {
      console.error(`${ErrorCodesUI.SortingError} - Error while sorting`);
			showErrorSnackbar(dispatch, `${ErrorCodesUI.SortingError} - Error while sorting.`);
    }
  };

  const resetGridState = (gridRef: React.RefObject<DataGrid<any, any>>) => {
    setTimeout(() => {
      gridRef.current?.instance.pageIndex(0).then(() => {
        gridRef.current?.instance.selectRowsByIndexes([0]);
        setRecordsCount(dataGridRef.current?.instance.totalCount);
        dispatch(setBannerCount(recordsCount));
      });
    }, 300);
  };
  
  return (
    <div className="is-table">
      <div className="cui-content-page">
      {registeredApplications.length !== 0 ? (
        <DataGrid
          dataSource={userAccessibleApplications()}
          showBorders
          className="cui-c_table cui-c_table-dx datagrid-height"
          ref={dataGridRef}
          columnAutoWidth
          allowColumnResizing
          onOptionChanged={handleSortChanges}
          renderAsync={true}
        >
          <SearchPanel visible width="180px" />
          <Sorting mode="single" />
          <LoadPanel enabled={true} />
          <Scrolling mode="standard" rowRenderingMode="virtual" />
          <HeaderFilter visible={isFilterEnabled} allowSearch />
          <Column
            cellRender={setNameColumn}
            caption="Application Name"
            dataField="applicationName"
            sortIndex={0}
            sortOrder="asc"
            width={columnWidths[0]}
          />
          <Column
            cellRender={renderAdminFeatureColumn}
            caption="Admin Feature Name"
            alignment="left"
            dataField="features[0].feature"
            width={columnWidths[1]}
          >
            <HeaderFilter dataSource={adminFeatureFilterData}/>
          </Column>
          <Column
            cellRender={renderViewFeatureColumn}
            caption="View Application Feature Name"
            alignment="left"
            dataField="features[1].feature"
            width={columnWidths[2]}
          >
            <HeaderFilter dataSource={viewApplicationFeatureFilterData} />
          </Column>
          <Column
            cellRender={renderExecutiveReportsFeatureColumn}
            caption="View Executive Reports Feature Name"
            alignment="left"
            dataField="features[2].feature"
            width={columnWidths[3]}
          >
            <HeaderFilter dataSource={executiveReportsFeatureFilterData} />
          </Column>
          <Column
            caption="Status"
            dataField="isActiveText"
            width={columnWidths[4]}
            alignment = "center"
          >
          </Column>
          <Column
            caption="Action"
            cellRender={setActionColumn}
            allowSearch={false}
            allowSorting={false}
            allowFiltering={false}
            width={columnWidths[5]}
          />
          <Toolbar>
            <Item location="before">
              <div className="page-title-container">
                <PageHeader text="Application Manager" />
              </div>
            </Item>
            <Item location="after">
              <Button onClick={handleFilter}>
                <FilterListIcon sx={{ fontSize: 15, margin: '5px' }} />
                {!isFilterEnabled ? 'Show' : 'Hide'}
              </Button>
              <ClearFiltersButton gridRef={dataGridRef} />
            </Item>
            <Item name="searchPanel" />
            <Item location="after">
              <Button
                icon="add"
                text={registerApplicationText}
                type="default"
                stylingMode="contained"
                onClick={enterCreationMode}
              />
            </Item>
          </Toolbar>
          <Paging 
            defaultPageSize={20} defaultPageIndex={0} onPageSizeChange={handlePageSizeChange}/>
          <Pager
            visible
            allowedPageSizes={[10, 20, 40]}
            displayMode="compact"
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
        </DataGrid>
      ) : (!appsLoaded ?
        <LoadIndicator className="loading-wheel"/> : 
        <EmptyGrid dataGridLabel="Application Manager" creationLabel={registerApplicationText}
            errorText={error} onClick={enterCreationMode} showBackButton={false}/>
      )}
      </div>
      <RightSideDrawer
        header={drawerTitle}
        isOpen={isInCreationMode}
        onCloseClick={onDrawerCloseClick}
      >
        {editStatus ? (
          <ApplicationEdit
              onCancelClick={setIsInCreationMode}
              initialData={rowData}
          />
      ) : (
          <div>
            <ApplicationRegister 
              onCancelClick={setIsInCreationMode} 
              onRegister={handleApplicationRegistered}
            />
          </div>
      )}
      </RightSideDrawer>
    </div>
  )
};

export default React.memo(Applications);
